import { forwardRef } from 'react';
import clsx from 'clsx';
import styles from './title.module.less';
import type { PolymorphicComponentPropWithRef, PolymorphicRef } from '../types';

export type TitleProps<As extends React.ElementType = 'h2'> =
  PolymorphicComponentPropWithRef<
    As,
    {
      uppercase?: boolean;
      text?: string;
      size?: 1 | 2 | 3 | 4 | 5 | 6 | '1' | '2' | '3' | '4' | '5' | '6';
    }
  >;

type TitleComponent = <As extends React.ElementType = 'h2'>(
  props: TitleProps<As>
) => JSX.Element;

export const Title: TitleComponent = forwardRef(function Title<
  As extends React.ElementType = 'h2'
>(
  { children, className, size, as, text, uppercase, ...rest }: TitleProps<As>,
  ref?: PolymorphicRef<As>
) {
  const Tag = as || 'h2';
  const sizeInt = Number(String(size || as).replace(/^h([1-6])$/i, '$1')) || 1;
  const classes = clsx(
    styles.title,
    styles[`size${Math.max(1, Math.min(6, sizeInt))}`],
    uppercase && styles.uppercase,
    className
  );

  return (
    <Tag className={classes} ref={ref} {...rest}>
      {text ? formatTextBreaks(text) : null}
      {children}
    </Tag>
  );
}) as TitleComponent; // Needed to tell Typescript this does not return ReactNode but acutally JSX.Element

function formatTextBreaks(text: string): string {
  return text
    .replace(/--/g, '\u{AD}') // Use unicode instead of &shy; to avoid dangerouslySetInnerHTML
    .replace(/__/g, '\u{A0}') // Use unicode instead of &nbsp; to avoid dangerouslySetInnerHTML
    .replace(/(\d)\s(\d)/g, '$1\u{A0}$2'); // Format number-space-number to automatically avoid breaking up numbers i.e. 100 000
}
